import hookFetch from "@/util/hook-fetch.mjs";

// Purpose: whitelisting access to window, so that we can prevent using
// global variables in app.

const properties = [
  // Injected from blitz-core
  "__BLITZ__",
  "gc",

  // Injected from blitz testing
  "__BLITZ_TEST_MOCKS__",

  // Required for hash values in entry points
  "__BLITZ_ENTRY_POINTS__",

  "__BLITZ_LOG_FILTER__",
  "__BLITZ_LOG_STYLED__",

  // Build-time vars
  "__BLITZ_CN__",
  "__BLITZ_TESTING__", // subtly different build-time var

  // Built-ins
  "document",
  "navigator",
  "location",
  "addEventListener",
  "removeEventListener",
  "focus",
  "dispatchEvent",
  "CustomEvent",
  "requestAnimationFrame",
  "cancelAnimationFrame",
  "requestIdleCallback",
  "cancelIdleCallback",
  "open",
  "performance",
  "history",
  "fetch",
  "devicePixelRatio",
  "getComputedStyle",
  "matchMedia",
  "sessionStorage", // for persisting navigation direction
  "localStorage", // for accessing user settings from web
  "crypto",
  "URL",
  "Blob",
  "CompressionStream",
  "Response",

  // Third-party: ToS and privacy policy
  "uspapi",
  "tcfapi",
] as const;

const __BLITZ_DEV__ = {};

// Used to later generate types automagically
type WhitelistGlobalThProperties = ValuesOfTuple<typeof properties>;

// These are primitive values that we want to read from window.
// Do not put functions in here, instead list built-in functions above in
// `properties`, otherwise functions will be redefined here and typeof
// checks will not work.
const readOnlyWindowProperties = [
  "scrollX",
  "scrollY",
  "innerWidth",
  "innerHeight",
] as const;

// These exist on window and not globalThis, we are not binding these by iteration
type WhitelistWindowProperties = (typeof readOnlyWindowProperties)[number];

// These are additional global properties that may be set by the environment
type BlitzGlobalProperties = {
  // Injected from blitz-core.
  __BLITZ__?: BlitzMessaging;

  __BLITZ_TEST_MOCKS__?: BlitzMessaging & { EVENTS: BlitzEvents };

  __BLITZ_LOG_FILTER__?: string[];
  __BLITZ_LOG_STYLED__?: boolean;

  __BLITZ_ENTRY_POINTS__?: string[];

  // exists sometimes
  __BLITZ_DEV__?: Record<PrimitiveKey, unknown>;
};

type GlobalWhitelist = Readonly<
  PickSome<
    Window & typeof globalThis,
    WhitelistGlobalThProperties | WhitelistWindowProperties
  >
> &
  BlitzGlobalProperties;

const globals = {
  __BLITZ_DEV__,
} as GlobalWhitelist;
for (const prop of readOnlyWindowProperties) {
  Object.defineProperty(globals, prop, {
    enumerable: true,
    get() {
      /* eslint-disable no-restricted-properties */
      return globalThis[prop];
      /* eslint-enable no-restricted-properties */
    },
  });
}

for (const key of properties) {
  Object.defineProperty(globals, key, {
    enumerable: true,
    get() {
      let value = (globalThis as unknown)[key];
      if (key === "fetch") {
        return function (...args: unknown[]) {
          hookFetch(...args);
          let result = value.apply(globalThis, args);

          // Shitty heuristic to check if this is the native fetch function.
          // The reason why this is needed, is because fetch shims may not
          // handle this catch and rethrow behavior correctly.
          if (/\[.*?\]/.test(value.toString())) {
            result = result.catch((error) => {
              // Rewrite the error message to include the original URL.
              error.message = `${error.message} ${args[0]}`;

              throw error;
            });
          }
          return result;
        };
      }
      if (typeof value === "function") {
        value = value.bind(globalThis);
      }
      return value;
    },
  });
}

export default globals;
